import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TableWithState from "../../components/common/TableWithState";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import { getRequirements } from "../../api/requirements";
import Button from "../../ui/Button";

export default function RequirementList({ frameworkId }) {
  const navigate = useNavigate();

  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("display_order:asc");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["requirements", { frameworkId, limit, offset, q, sort }],
    queryFn: () => getRequirements(frameworkId, { limit, offset, q, sort }),
    enabled: Boolean(frameworkId),
    keepPreviousData: true,
  });

  const columns = useMemo(
    () => [
      { label: "Order", accessor: "display_order", sortable: true },
      { label: "Code", accessor: "code", sortable: true },
      { label: "Title", accessor: "title", sortable: true },
    ],
    []
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
  };

  if (!frameworkId) {
    return <EmptyState title="No framework selected" />;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
        <h2>Requirements</h2>
        <Button
          type="button"
          data-testid="add-requirement"
          onClick={() => navigate(`/frameworks/${frameworkId}/requirements/new`)}
          variant="primary"
        >
          Add Requirement
        </Button>
      </div>
      <form onSubmit={handleSearch} style={{ marginBottom: "12px", display: "flex", gap: "8px" }}>
        <input
          data-testid="requirement-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search requirements"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>
      {isLoading && <Loader />}
      {isError && <div>Error: {error?.detail || "Failed to load requirements"}</div>}
      {data && (
        <TableWithState
          columns={columns}
          data={data.items}
          total={data.total}
          limit={data.limit}
          offset={data.offset}
          sort={sort}
          onSort={setSort}
          onPageChange={setOffset}
          onRowClick={(row) => navigate(`/requirements/${row.id}`)}
        />
      )}
      {!isLoading && data?.items?.length === 0 && <EmptyState title="No requirements found" />}
    </div>
  );
}
